<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
    <div class="w-full max-w-sm bg-surface dark:bg-surface-dark rounded-xl shadow-xl">
      <div class="px-6 py-5 border-b border-stroke-subtle dark:border-stroke-dark text-center">
        <img v-if="logoUrl" :src="logoUrl" class="h-16 w-16 mx-auto mb-2" alt="" />
        <h2 class="text-base font-semibold text-fg-base dark:text-fg-base-dark">
          Welcome{{ firstName ? `, ${firstName}` : '' }}!
        </h2>
        <p class="mt-1 text-sm text-fg-secondary dark:text-fg-secondary-dark">This is your dashboard — set a password to secure it.</p>
      </div>

      <form @submit.prevent="submit" class="px-6 py-4 space-y-4">
        <!-- Shown (not hidden) so every browser's password manager reliably pairs
             the saved password with this email as the username — Safari in
             particular is unreliable about saving credentials from hidden fields. -->
        <div>
          <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Email</label>
          <input
            type="email"
            :value="email"
            autocomplete="username"
            readonly
            class="w-full px-3 py-2 rounded-lg border border-stroke-subtle dark:border-stroke-dark bg-surface-muted dark:bg-surface-muted-dark text-fg-secondary dark:text-fg-secondary-dark text-sm cursor-default"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">New password</label>
          <input
            v-model="newPassword"
            type="password"
            autocomplete="new-password"
            required
            minlength="8"
            autofocus
            class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label class="block text-sm font-medium text-fg-secondary dark:text-fg-secondary-dark mb-1">Confirm password</label>
          <input
            v-model="confirmPassword"
            type="password"
            autocomplete="new-password"
            required
            class="w-full px-3 py-2 rounded-lg border border-stroke-strong dark:border-stroke-strong-dark bg-surface dark:bg-surface-muted-dark text-fg-base dark:text-fg-base-dark text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <p v-if="error" class="text-sm text-red-600 dark:text-red-400">{{ error }}</p>

        <button
          type="submit"
          :disabled="loading"
          class="w-full py-2 px-4 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg transition-colors"
        >
          {{ loading ? 'Setting password…' : 'Set password & continue' }}
        </button>
      </form>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'
import { useTenantBranding } from '@/composables/useTenantBranding'

const props = defineProps({
  name: { type: String, default: '' },
  email: { type: String, default: '' },
})
const emit = defineEmits(['done'])

const { logoUrl } = useTenantBranding()

const firstName = computed(() => props.name?.split(' ')[0] || '')

const newPassword = ref('')
const confirmPassword = ref('')
const loading = ref(false)
const error = ref('')

const submit = async () => {
  error.value = ''

  if (newPassword.value.length < 8) {
    error.value = 'Password must be at least 8 characters.'
    return
  }
  if (newPassword.value !== confirmPassword.value) {
    error.value = 'Passwords do not match.'
    return
  }

  loading.value = true
  try {
    const res = await fetch('/api/auth/set-initial-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ new_password: newPassword.value }),
    })
    const data = await res.json()
    if (!res.ok) {
      error.value = data.detail || 'Something went wrong.'
      return
    }
    emit('done')
  } catch {
    error.value = 'Something went wrong. Please try again.'
  } finally {
    loading.value = false
  }
}
</script>
